import React from "react";
import { COLORS } from "../../../theme/colors";

// Loop-edge label geometry, extracted so it's unit-testable.
export const LOOP_LABEL_W = 76;   // rect width  (must match the <rect> below)
export const LOOP_LABEL_H = 16;   // rect height (must match the <rect> below)

// Position a loop-edge label ON its own curve, near the SOURCE node it returns
// FROM (t≈0.32), so the label stays with the loop it labels instead of pooling
// at the shared target where parallel labels stacked. Evaluates the cubic Bézier
// the edge is drawn with (control points ±40 / +dropY) at parameter t.
export function loopLabelPosition(from, to, dropY, t = 0.32) {
  const mt = 1 - t;
  const c1x = from.x + 40, c1y = from.y + dropY;
  const c2x = to.x - 40, c2y = to.y + dropY;
  const x = mt * mt * mt * from.x + 3 * mt * mt * t * c1x + 3 * mt * t * t * c2x + t * t * t * to.x;
  const y = mt * mt * mt * from.y + 3 * mt * mt * t * c1y + 3 * mt * t * t * c2y + t * t * t * to.y;
  return { x, y };
}

// Curve-depth stagger per loop index sharing a target. Large enough that the
// per-index label-Y separation (~0.68·Δdrop at t≈0.32) always exceeds the label
// height, so parallel returns to the same node never overlap.
export const LOOP_DROP_BASE = 80;
export const LOOP_DROP_STEP = 40;

const CanvasConnections = ({ nodes, connections, mousePos, connectingFrom, selectedConnection, onSelectConnection, reconnectDrag, onReconnectStart }) => {
  const getNodePort = (id, side) => {
    const n = nodes.find(n => n.id === id);
    if (!n) return { x: 0, y: 0 };
    const hub = n.type === "llm";
    const w = hub ? 198 : 158;
    const h = hub ? 44 : 30;
    return { x: side === "right" ? n.x + w : n.x, y: n.y + h };
  };
  return (
    <svg style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
      <defs>
        <linearGradient id="connGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={COLORS.accent} stopOpacity="0.6" />
          <stop offset="100%" stopColor={COLORS.cyan} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="connGradActive" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={COLORS.green} stopOpacity="0.8" />
          <stop offset="100%" stopColor={COLORS.cyan} stopOpacity="0.8" />
        </linearGradient>
        <linearGradient id="connGradSelected" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={COLORS.pink} stopOpacity="0.9" />
          <stop offset="100%" stopColor={COLORS.orange} stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id="connGradParallel" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={COLORS.purple} stopOpacity="0.7" />
          <stop offset="100%" stopColor={COLORS.pink} stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="connGradConditional" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={COLORS.orange} stopOpacity="0.7" />
          <stop offset="100%" stopColor={COLORS.purple} stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id="connGradLoop" x1="100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%" stopColor={COLORS.orange} stopOpacity="0.7" />
          <stop offset="100%" stopColor={COLORS.accent} stopOpacity="0.7" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <marker id="arrowLoop" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <path d="M0,0 L8,3 L0,6" fill={COLORS.orange} opacity="0.7" />
        </marker>
      </defs>
      {(() => {
        // Pre-compute loop indices per target node so multiple return
        // loops to the same target (e.g., 5 experts → 1 supervisor in
        // fan_out) fan out instead of stacking on top of each other.
        const loopIdxByTarget = new Map();
        const loopIdx = new Map();
        (connections || []).forEach((c, i) => {
          if (!c || c.type !== "loop") return;
          const idx = loopIdxByTarget.get(c.to) || 0;
          loopIdx.set(i, idx);
          loopIdxByTarget.set(c.to, idx + 1);
        });
        return connections.map((c, i) => {
        const isSel = selectedConnection === i;
        const isReconnecting = reconnectDrag && reconnectDrag.connIndex === i;
        const isLoop = c.type === "loop";

        if (isLoop) {
          // Loop connection: curved arc below the nodes (from right port → down → back to left port)
          const from = getNodePort(c.from, "right");
          const to = getNodePort(c.to, "left");
          // Stagger dropY per loop index sharing this target so parallel
          // returns (fan-out / sequential ownership returns) don't pile.
          const dropY = LOOP_DROP_BASE + (loopIdx.get(i) || 0) * LOOP_DROP_STEP;
          const pathD = `M${from.x},${from.y} C${from.x + 40},${from.y + dropY} ${to.x - 40},${to.y + dropY} ${to.x},${to.y}`;
          // Keep the label with its OWN loop (near the source it returns from)
          // rather than at the shared-target midpoint where parallel labels
          // overlapped; the dropY stagger guarantees same-target returns separate.
          const { x: labelX, y: labelY } = loopLabelPosition(from, to, dropY);
          return (
            <g key={i}>
              <path
                d={pathD}
                fill="none" stroke="transparent" strokeWidth="14"
                style={{ pointerEvents: "stroke", cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); onSelectConnection(i); }}
              />
              <path
                d={pathD}
                fill="none"
                stroke={isSel ? "url(#connGradSelected)" : "url(#connGradLoop)"}
                strokeWidth={isSel ? 3 : 2}
                strokeDasharray="6 3"
                opacity={isSel ? 0.9 : 0.6}
                filter={isSel ? "url(#glow)" : "none"}
                markerEnd="url(#arrowLoop)"
              />
              {/* Animated flow dot */}
              <circle r="3" fill={COLORS.orange} opacity="0.7">
                <animateMotion dur="2.5s" repeatCount="indefinite" path={pathD} />
              </circle>
              {/* Label */}
              {c.label && (
                <g>
                  <rect
                    x={labelX - LOOP_LABEL_W / 2} y={labelY - LOOP_LABEL_H / 2}
                    width={LOOP_LABEL_W} height={LOOP_LABEL_H} rx="4"
                    fill={COLORS.surface} stroke={COLORS.orange} strokeWidth="0.5" opacity="0.9"
                  />
                  <text x={labelX} y={labelY + 3} textAnchor="middle"
                    fill={COLORS.orange} fontSize="9" fontWeight="600" fontFamily="'DM Sans', sans-serif"
                    style={{ pointerEvents: "none" }}
                  >{c.label}</text>
                </g>
              )}
            </g>
          );
        }

        const from = getNodePort(c.from, "right");
        const to = isReconnecting && mousePos ? mousePos : getNodePort(c.to, "left");
        const mx = (from.x + to.x) / 2;
        const pathD = `M${from.x},${from.y} C${mx},${from.y} ${mx},${to.y} ${to.x},${to.y}`;
        return (
          <g key={i}>
            {/* Invisible wider hit area for clicking */}
            <path
              d={pathD}
              fill="none" stroke="transparent" strokeWidth="14"
              style={{ pointerEvents: "stroke", cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); onSelectConnection(i); }}
            />
            <path
              d={pathD}
              fill="none"
              stroke={isSel ? "url(#connGradSelected)" : c.type === "parallel" ? "url(#connGradParallel)" : c.type === "conditional" ? "url(#connGradConditional)" : "url(#connGrad)"}
              strokeWidth={isSel ? 3 : c.type === "parallel" ? 3 : 2}
              strokeDasharray={isReconnecting ? "8 4" : c.type === "conditional" ? "6 3" : "none"}
              opacity={isSel ? 0.9 : 0.5}
              filter={isSel ? "url(#glow)" : "none"}
            />
            {/* Animated flow dot */}
            {!isReconnecting && (
              <circle r="3" fill={isSel ? COLORS.pink : COLORS.accent} opacity="0.7">
                <animateMotion dur={`${2 + i * 0.3}s`} repeatCount="indefinite" path={pathD} />
              </circle>
            )}
            {/* Draggable endpoint handle on selected connection */}
            {isSel && !isReconnecting && (
              <circle
                cx={getNodePort(c.to, "left").x} cy={getNodePort(c.to, "left").y}
                r="7" fill={COLORS.pink} stroke="#fff" strokeWidth="2"
                style={{ pointerEvents: "all", cursor: "grab" }}
                opacity="0.9"
                onMouseDown={(e) => { e.stopPropagation(); onReconnectStart(i); }}
              />
            )}
          </g>
        );
        });
      })()}
      {/* Live connection line while dragging new connection */}
      {connectingFrom && mousePos && !reconnectDrag && (() => {
        const from = getNodePort(connectingFrom, "right");
        const mx = (from.x + mousePos.x) / 2;
        return (
          <path
            d={`M${from.x},${from.y} C${mx},${from.y} ${mx},${mousePos.y} ${mousePos.x},${mousePos.y}`}
            fill="none" stroke="url(#connGradActive)" strokeWidth="2.5"
            strokeDasharray="8 4" filter="url(#glow)" opacity={0.8}
          />
        );
      })()}
    </svg>
  );
};

export default CanvasConnections;
